/**
 * «My access» in the Bitrix24 iframe (#1645): the same read as the portal's
 * (`useAccessMe`), over the cookie session — `/api/v1/embedded/access/me`.
 *
 * Asked once, by `EmbeddedScreenSubjectProvider` (the embedded layout, under
 * the consent and access-request gates — not by the iframe's providers above
 * them); everything that decides by it reads `useScreenSubject`. The
 * ApiClient is the one `EmbeddedAuthProvider` already built for the session.
 */
import { useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { useAccessMe, type AccessViewer } from '@/hooks/useAccessMe';
import type { AccessState } from '@/lib/route-permissions';

/**
 * @param role the portal user's role in the organization — compared with the
 *   answer, a change of it closes what is open (E1.5 PR 4b). Nothing is
 *   compared while the session has not told it (`null`), so its arrival is
 *   not a change. The role is the value the session loaded at its start; a
 *   growth of the access revision re-reads the session quietly (no spinner, the
 *   open screen stays) and the comparison waits for the fresh role (Codex P1,
 *   #1745). Overlapping re-reads are the context's to order: only the newest
 *   one's answer is committed (`refreshSession`).
 */
export function useEmbeddedAccess(role: string | null = null): AccessState {
  const { apiClient, orgId, isLoading } = useAuth();
  const { refreshSession } = useEmbeddedOrg();
  const viewer = useMemo(
    () => (role === null ? null : { globalRole: role, isOwner: false }),
    [role]
  );
  const refreshViewer = useCallback(async (): Promise<AccessViewer | null> => {
    const fresh = await refreshSession({ quiet: true });
    // `undefined` — the session could not be read: the role is UNKNOWN, which
    // `useAccessMe` asks again and then fails closed on. `null` — the session
    // read fine and has no role: nothing to compare with.
    if (fresh === undefined) return null;
    return { globalRole: fresh, isOwner: false };
  }, [refreshSession]);
  return useAccessMe({
    getMe: () => apiClient.access.getMe(),
    orgId,
    subjectId: null,
    enabled: !isLoading,
    viewer,
    refreshViewer,
  });
}
